/**
 * @file user.tsx
 *
 * @fileoverview Exports user context, related interface and provider.
 */
import {
  Context,
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import useCredits, {
  UseCreditsInterface,
} from '~/jobiqo/components/Credits/hooks/useCredits';
import useSubscriptions, {
  UseSubscriptionsInterface,
} from '~/jobiqo/components/Subscriptions/hooks/useSubscriptions';
import { User } from '~/jobiqo/models/user';
import { OrganizationProfile } from '~/jobiqo/types/types';
import { Consent } from '../lib/cmp';
import {
  analyticsDispatchDelayed,
  DIMENSIONS,
  Event,
  EVENTS,
} from '../lib/analytics';
import { getUserDimensions } from '../components/User/utils/user_analytics';
import Router from 'next/router';
import { omitQueryParam, removeQueryParamFromUrl } from '~/jobiqo/lib/redirect';

export interface UserContextInterface {
  /**
   * The current user.
   */
  user?: User;
  /**
   * The authenticated flag.
   */
  authenticated: boolean;
  /**
   * The masqueraded flag.
   */
  masqueraded: boolean;
  /**
   * The user consents.
   */
  consents: Consent[];
  /**
   * Sets consents.
   */
  setConsents: Dispatch<SetStateAction<Consent[]>>;
  /**
   * If the user is recruiter, this will be currently selected organization
   * profile in the organization switcher. This will be undefined otherwise.
   */
  currentOrganizationProfile?: OrganizationProfile;
  /**
   * Sets current organization profile, used in the organization switcher.
   */
  setCurrentOrganizationProfile: Dispatch<
    SetStateAction<OrganizationProfile | undefined>
  >;
  /**
   * If the user is recruiter, this will be all the organization
   * profiles in the organization switcher.
   */
  updatableOrganizationProfiles: OrganizationProfile[];
  /**
   * Sets updatable organization profiles, used in the organization switcher.
   */
  setUpdatableOrganizationProfiles: Dispatch<
    SetStateAction<OrganizationProfile[]>
  >;
  /**
   * The user credits.
   */
  credits: UseCreditsInterface;
  /**
   * The user subscriptions.
   */
  userSubscriptions: UseSubscriptionsInterface;
  /**
   * The reset context function.
   * Fetches user subscriptions and credits.
   */
  resetUserContext: () => void;
}

/**
 * Initialize user context.
 */
export const UserContext: Context<UserContextInterface> = createContext<
  UserContextInterface
>({
  authenticated: false,
  masqueraded: false,
  consents: [],
  setConsents: () => {},
  setCurrentOrganizationProfile: () => {},
  updatableOrganizationProfiles: [],
  setUpdatableOrganizationProfiles: () => {},
  userSubscriptions: {
    data: [],
    loading: false,
    Error: null,
    total: 0,
  },
  credits: { data: [], loading: false, Error: null },
  resetUserContext: () => {},
});

interface UserProviderProps
  extends Pick<UserContextInterface, 'user' | 'authenticated' | 'masqueraded'> {
  /**
   * The children elements.
   */
  children: React.ReactNode;
}

/**
 * Resolves the router dispatch event.
 *
 * @param event
 *   The event to dispatch.
 * @param dimension
 *   The user dimension.
 * @returns void
 */
function resolveRouterDispatch(event: Event, dimension: any) {
  // Prepare path to be used in Router.push. Router.asPath represents the translated URL (in case the path
  // translation is active).
  const pathWithoutDispatch = removeQueryParamFromUrl(
    removeQueryParamFromUrl(Router.asPath, 'dispatch'),
    DIMENSIONS.signup_method,
  );
  const queryWithoutDispatch = omitQueryParam(
    omitQueryParam(Router.query, 'dispatch'),
    DIMENSIONS.signup_method,
  );
  const signupMethod = Router.query[DIMENSIONS.signup_method];

  // Delete right away from querystring so we don't fire events twice.
  delete Router.query.dispatch;
  delete Router.query[DIMENSIONS.signup_method];

  Router.push(
    {
      pathname: Router.pathname,
      query: queryWithoutDispatch,
    },
    pathWithoutDispatch,
    { shallow: true },
  ).then(() => {
    analyticsDispatchDelayed(
      event,
      getUserDimensions(
        dimension,
        typeof signupMethod === 'string' ? signupMethod : undefined,
      ),
    );
  });
}

export default function UserProvider({
  children,
  user,
  authenticated,
  masqueraded,
}: UserProviderProps) {
  const credits: UseCreditsInterface = useCredits(user);
  const userSubscriptions: UseSubscriptionsInterface = useSubscriptions({
    skip: !user,
  });
  const [consents, setConsents] = useState<Consent[]>([]);
  // Currently selected organization profile for the recruiter.
  const [currentOrganizationProfile, setCurrentOrganizationProfile] = useState<
    OrganizationProfile
  >();
  // All the updatable organization profiles for the current user.
  const [
    updatableOrganizationProfiles,
    setUpdatableOrganizationProfiles,
  ] = useState<OrganizationProfile[]>([]);

  useEffect(() => {
    const dispatchQueryValue = Router.query.dispatch;
    if (authenticated && user && dispatchQueryValue) {
      switch (dispatchQueryValue) {
        case EVENTS.SIGNUP_START.key:
          resolveRouterDispatch(EVENTS.SIGNUP_START, user);
          break;
        case EVENTS.LOGIN.key:
          resolveRouterDispatch(EVENTS.LOGIN, user);
          break;
      }
    }
  }, [authenticated]);

  return (
    <UserContext.Provider
      value={{
        consents,
        setConsents,
        user,
        authenticated,
        masqueraded,
        currentOrganizationProfile,
        setCurrentOrganizationProfile,
        updatableOrganizationProfiles,
        setUpdatableOrganizationProfiles,
        credits,
        userSubscriptions,
        resetUserContext: () => {
          credits.refetch?.();
          userSubscriptions.refetch?.();
        },
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export { UserProvider };
